import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import api from "@/lib/api";
import { Trash2 } from "lucide-react";
import { toast } from "sonner";
import { formatApiError } from "@/lib/utils2";

/**
 * Click-to-edit a scheduled maintenance task. Fields: task name, project,
 * interval/unit, recurring, reminder, notes, due date, then save / delete.
 */
export default function EditScheduleDialog({ schedule, onClose, onSaved }) {
  const [projects, setProjects] = useState([]);
  const dueDateOnly = (schedule.due_date || "").slice(0, 10);
  const [form, setForm] = useState({
    task: schedule.task || "",
    project_id: schedule.project_id || "",
    interval: schedule.interval || 30,
    unit: schedule.unit || "days",
    recurring: schedule.recurring !== false,
    reminder_on: schedule.reminder_on !== false,
    notes: schedule.notes || "",
    due_date: dueDateOnly,
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/projects").then(({ data }) => setProjects(data.items)).catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.task.trim()) {
      toast.error("Task name is required");
      return;
    }
    setSaving(true);
    try {
      // Preserve original time-of-day on the new date
      const orig = new Date(schedule.due_date);
      const dueDate = new Date(form.due_date);
      dueDate.setHours(orig.getHours() || 9, orig.getMinutes() || 0, 0, 0);

      await api.put(`/schedule/${schedule.schedule_id}`, {
        task: form.task,
        project_id: form.project_id || null,
        interval: parseInt(form.interval) || 30,
        unit: form.unit,
        recurring: form.recurring,
        reminder_on: form.reminder_on,
        notes: form.notes || null,
        due_date: dueDate.toISOString(),
      });
      toast.success("Task updated");
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const onDelete = async () => {
    if (!window.confirm("Delete this scheduled task?")) return;
    try {
      await api.delete(`/schedule/${schedule.schedule_id}`);
      toast.success("Task removed");
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <Dialog open={true} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-zinc-900 border-zinc-800 text-zinc-100 max-w-xl rounded-sm">
        <DialogHeader>
          <div className="text-[10px] uppercase tracking-[0.3em] text-orange-500 mb-1">// Edit task</div>
          <DialogTitle className="font-display font-black text-2xl tracking-tight">Scheduled task</DialogTitle>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-4 mt-2">
          <div>
            <Label className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Task</Label>
            <Input
              required
              value={form.task}
              onChange={(e) => setForm({ ...form, task: e.target.value })}
              className="mt-1 bg-zinc-950 border-zinc-800 focus:border-orange-500 rounded-sm"
              data-testid="edit-task-name"
            />
          </div>

          <div>
            <Label className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Project</Label>
            <Select
              value={form.project_id || "none"}
              onValueChange={(v) => setForm({ ...form, project_id: v === "none" ? "" : v })}
            >
              <SelectTrigger className="mt-1 bg-zinc-950 border-zinc-800 rounded-sm" data-testid="edit-task-project">
                <SelectValue placeholder="None" />
              </SelectTrigger>
              <SelectContent className="bg-zinc-900 border-zinc-800">
                <SelectItem value="none">No project</SelectItem>
                {projects.map((p) => (
                  <SelectItem key={p.project_id} value={p.project_id}>{p.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {projects.length === 0 && (
              <div className="text-[10px] uppercase tracking-widest text-zinc-600 mt-1">
                Create projects from the Projects page first
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <Label className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Due date</Label>
              <Input
                type="date"
                value={form.due_date}
                onChange={(e) => setForm({ ...form, due_date: e.target.value })}
                className="mt-1 bg-zinc-950 border-zinc-800 rounded-sm font-mono"
                data-testid="edit-task-date"
              />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Every</Label>
              <Input
                type="number"
                min={1}
                value={form.interval}
                onChange={(e) => setForm({ ...form, interval: e.target.value })}
                className="mt-1 bg-zinc-950 border-zinc-800 rounded-sm font-mono"
                data-testid="edit-task-interval"
              />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Unit</Label>
              <Select value={form.unit} onValueChange={(v) => setForm({ ...form, unit: v })}>
                <SelectTrigger className="mt-1 bg-zinc-950 border-zinc-800 rounded-sm" data-testid="edit-task-unit">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-zinc-900 border-zinc-800">
                  <SelectItem value="days">Days</SelectItem>
                  <SelectItem value="hours">Hours</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="flex items-center justify-between bg-zinc-950 border border-zinc-800 rounded-sm px-3 py-2">
              <div>
                <Label className="text-sm">Recurring</Label>
                <div className="text-[10px] uppercase tracking-widest text-zinc-600">Auto-reschedule on done</div>
              </div>
              <Switch
                checked={form.recurring}
                onCheckedChange={(v) => setForm({ ...form, recurring: v })}
                data-testid="edit-task-recurring"
              />
            </div>
            <div className="flex items-center justify-between bg-zinc-950 border border-zinc-800 rounded-sm px-3 py-2">
              <div>
                <Label className="text-sm">Reminders</Label>
                <div className="text-[10px] uppercase tracking-widest text-zinc-600">Show in upcoming</div>
              </div>
              <Switch
                checked={form.reminder_on}
                onCheckedChange={(v) => setForm({ ...form, reminder_on: v })}
                data-testid="edit-task-reminder"
              />
            </div>
          </div>

          <div>
            <Label className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Notes</Label>
            <Textarea
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              className="mt-1 bg-zinc-950 border-zinc-800 rounded-sm"
              rows={2}
              data-testid="edit-task-notes"
            />
          </div>

          <DialogFooter className="gap-2 sm:justify-between">
            <Button
              type="button"
              variant="ghost"
              onClick={onDelete}
              className="text-red-500 hover:text-red-400 hover:bg-red-950/30 sm:mr-auto"
              data-testid="edit-task-delete"
            >
              <Trash2 className="w-4 h-4 mr-1" /> Delete
            </Button>
            <div className="flex gap-2">
              <Button type="button" variant="ghost" onClick={onClose} className="text-zinc-400">Cancel</Button>
              <Button type="submit" disabled={saving} className="bg-orange-600 hover:bg-orange-500 rounded-sm uppercase tracking-widest text-xs font-bold" data-testid="edit-task-save">
                {saving ? "Saving..." : "Save changes"}
              </Button>
            </div>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
